import { animate, style, transition, trigger } from '@angular/animations';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, timer } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { BrowserSnifferService } from '../../services/browser-sniffer.service';
import { SharedUtilities } from '../../shared/utils/shared-utilities';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
// import { MaterialModule } from '@tt-lib/material.module';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, Photo } from '@capacitor/camera';

/**
 * An interface to bin files and valiation errors
 */
export interface UploaderFile {
  file: File;
  errors: Array<string>;
}

@Component({
  selector: 'tt-file-uploader',
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatListModule
  ],
  templateUrl: './file-uploader.component.html',
  styleUrls: ['./file-uploader.component.scss'],
  animations: [
    trigger('fade', [
      transition('void => *', [
        style({ opacity: 0 }),
        animate(1000, style({ opacity: 1 }))
      ])
    ])
  ],
  standalone: true
})
export class FileUploaderComponent implements OnInit {

  /**
   * Viewchild used to retrieve files imported with the "select files" button
   */
  @ViewChild('inputSelectFileElement')
  private _inputSelectFileElement: ElementRef<HTMLInputElement>;

  /**
   * Viewchild used to retrieve files imported with the "capture" button
   */
  @ViewChild('inputCaptureFileElement')
  private _inputCaptureFileElement: ElementRef<HTMLInputElement>;

  /**
   * Modifies the style of the import box on over 
   * @param ev drag event (over)
   */
  @HostListener('dragover', ['$event']) _onDragOver(ev: DragEvent) {
    ev.preventDefault();
    this.isDraggingFile = true;
  }

  /**
   * Modifies the style of the import box on leave 
   * @param ev drag event (leave) 
   */
  @HostListener('dragleave', ['$event']) _onDragLeave(ev: DragEvent) {
    ev.preventDefault();
    this.isDraggingFile = false;
  }

  /**
   * Handles the imports via drag&drop
   * @param ev drag event (drop) 
   */
  @HostListener('drop', ['$event']) _onDrop(ev: DragEvent) {
    ev.preventDefault();
    this.isDraggingFile = false;
    this.importFiles(Array.from(ev.dataTransfer.files));
    this.onDragEvent.emit(ev);
  }

  // optional inputs
  @Input() accept: string;
  @Input() fileButtonText: string = 'file-uploader.upload-files';
  @Input() fileButtonMobileText: string = 'file-uploader.upload-files-capture-mobile';
  @Input() extraInfoWithDropFunctionText: string = 'file-uploader.extra-info-with-drop-function';
  @Input() loadingText: string = 'file-uploader.loading';
  @Input() loadingFailedText: string;
  @Input() multiple: boolean = false;
  @Input() showFileList: boolean = false;
  @Input() allowedExtensions: Array<string>;
  @Input() maxUploadSizePerFile: number = 52428800;
  @Input() maxFilesToUpload: number;
  @Input() capturesEnabled: boolean = false;

  @Output() onDragEvent = new EventEmitter<DragEvent>();
  @Output() onImportEvent = new EventEmitter<ElementRef<HTMLInputElement>>();
  @Output() onErrorEvent = new EventEmitter<string>();

  public isDraggingFile: boolean;
  public isLoadingFile: boolean = false;
  public isLoadingFileError: boolean = false;
  public errorMessage: String = "";

  protected importedFiles: Array<UploaderFile> = [];

  constructor(
    public browsersniffer: BrowserSnifferService,
    public translateService: TranslateService
  ) { }


  /**
   * Initial configuration
   */
  ngOnInit(): void {
    if (!this.multiple) {
      this.maxFilesToUpload = 1;
      this.showFileList = false;
    }

    this.handleErrorEvent().subscribe();
  }

  /**
   * 
   * @param isCameraRequest if true, then check if we can use Capacitor Camera plugin for taking pictures; if false, then check if we can use Capacitor Filesystem plugin for selecting files
   * @returns 
   */
  useCapacitorPlugin(isCameraRequest: boolean) {
    switch (Capacitor.getPlatform()) {
      case 'ios': {
        //nothing used in ios yet
        return isCameraRequest ? false : false;
      }
      case 'android': {
        //only camera plugin is used at this moment
        return isCameraRequest ? true : false;
      }
    }
    return false;
  }

  takePicture() {
    /*
    const takePicture = async () => {
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: true,
        resultType: CameraResultType.Uri
      })

      // image.webPath will contain a path that can be set as an image src.
      // You can access the original file using image.path, which can be
      // passed to the Filesystem API to read the raw data of the image,
      // if desired (or pass resultType: CameraResultType.Base64 to getPhoto)
      var imageUrl = image.webPath;
      console.log(imageUrl)
    };
    */
    Camera.getPhoto({
      quality: 100,
      allowEditing: false,
      resultType: CameraResultType.DataUrl
    }).then((pic: Photo) => {
      /*
      let isAllowedExtensions = false;
      this.allowedExtensions?.every(extension => {
        isAllowedExtensions = extension.toLowerCase() === pic.format.toLowerCase()
        return !isAllowedExtensions
      });
      if(!isAllowedExtensions){
        console.log("extension not allowed : " + pic.format);
      }
      */
      this.importFiles([this.dataURLtoFile(pic.dataUrl, 'capture_' + Date.now() + '.' + pic.format)]);
      this.onImportEvent.emit();
    }).catch((error: any) => {
      console.log(error)
    });
  }
  private dataURLtoFile(dataurl, filename) {
    var arr = dataurl.split(','),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[arr.length - 1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  }

  /**
   * Executed with a classic file import (via button)
   * @param event 
   */
  onSelectImport(event: Event): void {
    this.importFiles(Array.from(this._inputSelectFileElement.nativeElement.files));
    this.onImportEvent.emit();
  }

  /**
   * Executed with a capture (via button)
   * @param event 
   */
  onCaptureImport(event: Event): void {
    //console.log('onCaptureImport...')
    let captures: Array<File> = Array.from(this._inputCaptureFileElement.nativeElement.files);
    let renamedCaptures: Array<File> = new Array();
    captures.forEach((file: File) => {
      let originalExtension: string = file.name.split('.').pop();
      let fileToAdd: File = this.renameFile([file], 'capture_' + Date.now() + '.' + originalExtension);
      renamedCaptures.push(fileToAdd);
    })
    this.importFiles(renamedCaptures);
    this.onImportEvent.emit();
  }

  /**
   * Removes file input, imported files array and form control.
   */
  clearFileList(): void {
    this._inputSelectFileElement.nativeElement.value = '';
    if (this._inputCaptureFileElement) {
      this._inputCaptureFileElement.nativeElement.value = '';
    }
    this.importedFiles = [];
  }

  /**
   * Getter for imported files
   * @returns an array of imported files
   */
  getFiles(): Array<File> {
    return this.importedFiles.map((uploaderFile: UploaderFile) => uploaderFile.file);
  }

  /**
   * Getter for imported files with errors
   * @returns an array of UploaderFiles 
   */
  getUploaderFiles(): Array<UploaderFile> {
    return this.importedFiles;
  }

  /**
   * Iterates through the list of uploaded files to find errors
   * @returns true if an error is found
   */
  filesContainErrors(): boolean {
    let errorFound = false;
    this.importedFiles.every((uf: UploaderFile) => {
      if (uf.errors.length > 0) {
        errorFound = true;
        return false;
      }
      return true;
    })
    return errorFound;
  }

  /**
   * The imported files can be imported from two different ways (d&d and via button),
   * this function combines the treatments common to both methods.
   * @param files an array of files to import
   */
  importFiles(files: Array<File>): void {
    this.startLoading();
    this.addFilesToUploader(files);
  }

  /**
   * Deletes the files specified in the list (by their names) 
   * @param filesToRemove table of file names to be deleted
   */
  removeFilesByName(filesToRemove: Array<string>): void {
    this.importedFiles = this.importedFiles.filter((file) => !filesToRemove.includes(file.file.name));
  }

  /**
   * Removes a file from importedFiles 
   * @param file uploader file
   */
  removeFromImportedFiles(file: UploaderFile): void {
    const index = this.importedFiles.indexOf(file);
    this.importedFiles.splice(index, 1);
  }

  /**
   * Shows an import error in the upload wrapper for a couple of seconds.
   */
  showImportError(textToDisplay?: string): void {
    this.onErrorEvent.emit(textToDisplay);
  }

  /**
   * Takes a file and looks for errors 
   * @param file file to validate
   * @returns a string list with errors 
   */
  validateFileAndReturnErrorMessages(file: File): Array<string> {
    let errors: Array<string> = [];

    if (this.allowedExtensions && this.allowedExtensions.length > 0 && !this.fileHasValidExtension(file)) {
      errors.push("file-uploader.invalid-extension")
    }

    if (this.maxUploadSizePerFile && !this.fileHasValidSize(file)) {
      errors.push("file-uploader.size-exceeded")
    }

    return errors;
  }

  /**
   * Takes an array of files and converts it to "UploaderFiles".
   * A validation is performed and a check for duplicate entries is performed.
   * Shows import error if extension invalid (if multiple mode is false)
   * @param files an array of files 
   */
  private addFilesToUploader(files: Array<File>): void {
    if (this.isFileNumberExceeded(files.length + this.importedFiles.length)) {
      this.showImportError("file-uploader.number-files-exceeded");
      this.resetLoading();
      return;
    }

    let invalidExtensionForSingleFile = false;
    files.every((file) => {

      if (!this.multiple && !this.fileHasValidExtension(file)) {
        invalidExtensionForSingleFile = true;
        return false;
      }
      let file_index = this.importedFiles.findIndex(x => x.file.name == file.name && x.file.size == file.size);

      if (file_index === -1 && file.name.split('.').pop().length > 0) {
        let fileToAdd: UploaderFile = {
          file: file,
          errors: this.validateFileAndReturnErrorMessages(file)
        };
        this.importedFiles.push(fileToAdd)
      }
      return true;
    });

    if (invalidExtensionForSingleFile) {
      this.showImportError("file-uploader.invalid-extension");
    } else {
      this.resetLoading();
    }
  }

  /**
   * Takes an error message and shows an error message temporarly when onErrorEvent is emitted.
   * @returns an observable to display error message to the end user.
   */
  handleErrorEvent(): Observable<any> {
    return this.onErrorEvent.pipe(
      map((errorMessage: string) => {
        this.isLoadingFileError = true;
        this.isLoadingFile = false;
        this.loadingFailedText = errorMessage ? errorMessage : 'file-uploader.loadingfailed';
        if (!this.multiple) {
          this.importedFiles = [];
        }
      }),
      switchMap((_: any) => timer(5000)),
      tap((_: any) => {
        this.loadingFailedText = undefined;
        this.isLoadingFileError = false;
      }),
    )
  }

  /**
   * Validates the length of an array of files. 
   * @param files 
   * @returns "true" if the files length is exceeded.
   */
  isFileNumberExceeded(filesCount: number): boolean {
    return filesCount > this.maxFilesToUpload
  }

  /**
   * Convinience method to reset the loading process.
   */
  resetLoading(): void {
    this.isLoadingFile = false;
  }

  /**
   * Convinience method to start the loading process.
   */
  startLoading(): void {
    this.isLoadingFile = true;
  }

  /**
   * Takes a file and returns "true" if the extension is valid.
   * @param file file to check.
   * @returns 
   */
  fileHasValidExtension(file: File): boolean {
    let extractedExtension: string = file.name.split(".").pop();
    if (extractedExtension.length <= 0) {
      return false;
    }

    return this.allowedExtensions.includes(extractedExtension.toLowerCase());
  }

  /**
   * Resets isLoadingFile & isLoadingFileError
   */
  back(): void {
    this.isLoadingFile = false;
    this.isLoadingFileError = false;
  }

  /**
   * Takes a file and returns "true" if the size is less or equal to the "maxUploadSizePerFile" property.
   * @param file file to check.
   * @returns "true" if the size is less or equal to "maxUploadSizePerFile" property.
   */
  private fileHasValidSize(file: File): boolean {
    return file.size <= this.maxUploadSizePerFile;
  }

  /**
   * Formats bytes to a user friendly string
   * @returns string
   */
  getMaxUploadSizePerFile(): string {
    return SharedUtilities.formatBytesToString(this.maxUploadSizePerFile);
  }

  /**
   * Renames a file
   * note: we can not rename a file directly, a new file must be created
   * @param bits 
   * @param name 
   * @returns renamed File
   */
  renameFile(bits, name) {
    try {
      return new File(bits, name);
    } catch (e) {
      console.log(e)
      throw new Error("Cannot rename file to " + name);
    }
  }

  /**
   * Formats a string array to a user friendly string
   * @param list 
   * @returns string
   */
  formatList(list: Array<string>): string {
    if (list.length <= 0) {
      throw new Error("Empty list provided");
    }

    return list.length == 1 ? list[0] :
      [list.slice(0, -1).join(", "), list[list.length - 1]]
        .join(" " + this.translateService.instant("general.and") + " ");
  }

}
